import { EditOutlined, InfoCircleOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Checkbox, Col, DatePicker, Form, Input, message, Radio, Row, Select, Tooltip, Upload, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { TOLERANCE_OPTIONS } from '../../constants/toleranceOptions';
import AutoOpenSelect from '../SelectComponent/AutoOpenSelect';

import { fetchAlloyDropdown, resetAlloyList, resetCurrentAlloyListPage } from '../../slices/alloy';
import { fetchCustomerDropdown, resetCurrentCustomerPage, resetCustomerList, fetchCustomerConversionList, fetchCustomerConversionValue } from '../../slices/customer';
import {
  fetchTemperDropdown,
  resetCurrentTemperPage,
  resetTempersList,
} from '../../slices/tempers';
import { fetchWorkOrderById, updateWorkOrder } from '../../slices/work-order';
import {
  fetchDieProfileById,
  fetchDieProfileDropdown,
  resetCurrentDieProfilePage,
  resetDieProfileData,
  resetDieProfileList,
} from './../../slices/die-profile';
import moment from 'moment';
import { fetchNalcoRateForQuotation } from '../../slices/nalco-rate';
import { createNumberValidationRule } from '../../utils/CreateNumberValidationRule';
import ExcessStockCount from './ExcessStockCount';
import { debounce } from 'lodash';
import { fetchjobWorkList, resetJobWorkList } from '../../slices/job-work';
import { fetchPackingModeList, resetPackingModeData } from '../../slices/packing';
import { fetchUserList, resetUserList } from '../../slices/user';

const { Option } = Select;

function EditWorkOrder() {
  const [searchDieProfile, setSearchDieProfile] = useState('');
  const [sameCustomer, setSameCustomer] = useState(false);
  const [currentWTKg, setCurrentWTKg] = useState('');
  const dispatch = useDispatch();
  const [btnLoading, setBtnLoading] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const { id } = useParams();
  const [searchAlloy, setSearchAlloy] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedDieProfile, setSelectedDieProfile] = useState(null);
  const [selectedAlloy, setSelectedAlloy] = useState([]);
  const [selectCustomer, setSelectCustomer] = useState(null);
  const [selectedTemper, setSelectedTemper] = useState([]);
  const [forceOpen, setForceOpen] = useState(false);
  const [showDropdownLoader, setShowDropdownLoader] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [initialValues, setInitialValues] = useState({});

  const { list: customerList } = useSelector(state => state.customer);
  const { data: packingModeList, loading: packingModeLoading } = useSelector(state => state.packingMode);
  const { list: userNameList } = useSelector(state => state.user);
  const {
    list: dieProfileList,
    loading: dieProfileLoading,
    currentPage: dieProfileCurrentPage,
    hasMore: dieProfileHasMore,
  } = useSelector(state => state.dieProfile);
  const {
    list: alloyList,
    loading: alloyLoading,
    currentPage: alloyCurrentPage,
    hasMore: alloyHasMore,
  } = useSelector(state => state.alloy);
  const {
    list: temperList,
    loading: temperLoading,
    hasMore: temperHasMore,
  } = useSelector(state => state.temper);
  const { jobWorkdata, loading: jobWorkLoading } = useSelector(state => state.jobWork);
  const [poCopyList, setPoCopyList] = useState([]);

  useEffect(() => {
    const orderDate = dayjs();
    const deliveryDate = orderDate.clone().add(12, 'days');
    form.setFieldsValue({
      order_date: orderDate,
      delivery_date: deliveryDate,
      workorder_type: 'In House',
    });
  }, [form]);

  const disablePastDates = current => {
    const currentOrderDate = form.getFieldValue('order_date') || moment();
    return (
      current &&
      current <
      moment.max(moment().startOf('day').add(1, 'days'), currentOrderDate)
    );
  };

  useEffect(() => {
    const fetchWorkOrder = async () => {
      try {
        const response = await dispatch(fetchWorkOrderById(id)).unwrap();
        if (response) {
          const responsedData = response.data;
          form.setFieldsValue({
            bill_to: responsedData.bill_to?.id || null,
            ship_to: responsedData.ship_to?.id || null,
            order_date: dayjs(responsedData.order_date),
            delivery_date: responsedData?.delivery_date ? dayjs(responsedData.delivery_date) : null,
            purchase_order_no: responsedData.purchase_order_no || '',
            purchase_order_date: dayjs(responsedData.purchase_order_date),
            packing_mode: responsedData.packing_mode || '',
            project_name: responsedData.project_name || '',
            nalco_type: responsedData.nalco_type || null,
            approved_by: responsedData?.approved_by || null,
            tolerance: responsedData.tolerance || '',
            remarks: responsedData.remarks || null,
            status: responsedData.status || '',
            order_no: responsedData.order_no || '',
            po_copy: responsedData.po_copy || '',
            workorder_type: responsedData?.workorder_type || 'In House',
            work_order_details:
              responsedData.work_order_details?.length > 0
                ? responsedData.work_order_details.map(detail => ({
                  id: detail.id || null,
                  die_profile: detail.die_profile
                    ? {
                      label: detail.die_profile.die_number,
                      value: detail.die_profile.id,
                    }
                    : undefined,
                  customer_reference_number: detail?.customer_reference_number || '',
                  alloy: detail?.alloy
                    ? {
                      label: `${detail?.alloy.alloy_code}${detail?.alloy.standard_name ? ' - ' + detail?.alloy?.standard_name : ''}`,
                      value: detail?.alloy.id,
                    }
                    : null,
                  temper: detail.temper
                    ? { label: `${detail.temper.temper_code_new}${detail.temper.temper_code_old ? ' - ' + detail.temper.temper_code_old : ''}`, value: detail.temper.id }
                    : undefined,
                  jobworks: detail?.jobworks ? detail.jobworks : [],
                  cutting: detail.cutting || false,
                  machining: detail.machining || false,
                  anodising: detail.anodising || false,
                  powder_coating: detail.powder_coating || false,
                  deburring: detail.deburring || false,
                  pvdf: detail.pvdf || false,
                  length: detail.length || null,
                  pieces: detail.pieces || null,
                  net_weight: detail.net_weight || null,
                  min_weight: detail.min_weight || null,
                  max_weight: detail.max_weight || null,
                  nalco_rate: detail.nalco_rate || null,
                  die_over_weight: detail.die_over_weight || false,
                  nalco_rate_change_reason:
                    detail.nalco_rate_change_reason || null,
                  modify_nalco_rate: detail.modify_nalco_rate,
                  packing_charge: detail.packing_charge || null,
                  conversion: detail.conversion || null,
                  description: detail.description || '',
                  anodising_surface_treatment:
                    detail.anodising_surface_treatment || '',
                  anodising_description: detail.anodising_description || '',
                  anodising_price: detail.anodising_price || null,
                  powder_coating_surface_treatment:
                    detail.powder_coating_surface_treatment || '',
                  powder_coating_description:
                    detail.powder_coating_description || '',
                  powder_coating_price: detail.powder_coating_price || null,
                  pvdf_surface_treatment: detail.pvdf_surface_treatment || '',
                  pvdf_description: detail.pvdf_description || '',
                  pvdf_price: detail.pvdf_price || null,
                  out_source:
                    detail.out_source != null ? detail.out_source : null,
                  wt_kg_p_mt: detail.die_profile?.wt_kg_p_mt || null,
                  cutting_price: detail.cutting_price || null,
                  machining_price: detail?.machining_price || null,
                  deburring_price: detail.deburring_price || null,
                  laser_marking_description:
                    detail.laser_marking_description || '',
                  laser_marking_price: detail.laser_marking_price || null,
                }))
                : [
                  {
                    die_profile: undefined,
                    customer_reference_number: '',
                    alloy: '',
                    temper: '',
                    jobworks: [],
                    cutting: false,
                    deburring: false,
                    machining: false,
                    anodising: false,
                    powder_coating: false,
                    pvdf: false,
                    length: null,
                    pieces: null,
                    net_weight: null,
                    nalco_rate: null,
                    nalco_rate_change_reason: null,
                    modify_nalco_rate: false,
                    packing_charge: null,
                    anodising_surface_treatment: '',
                    anodising_description: '',
                    anodising_price: null,
                    powder_coating_surface_treatment: '',
                    powder_coating_description: '',
                    powder_coating_price: null,
                    pvdf_surface_treatment: '',
                    pvdf_description: '',
                    pvdf_price: null,
                    die_over_weight: false,
                    cutting_price: null,
                    machining_price: null,
                    deburring_price: null,
                    laser_marking_description: '',
                    laser_marking_price: null,
                    out_source: null,
                  },
                ],
          });

          form.setFieldsValue({
            customer: responsedData.ship_to?.id || null,
          });

          setPoCopyList(
            responsedData.po_copy
              ? [
                {
                  name: responsedData.po_copy,
                  url: `${responsedData.po_copy}`,
                  type: getFileType(responsedData.po_copy),
                },
              ]
              : [],
          );

          setSelectCustomer(responsedData.bill_to?.id);
          if (responsedData?.work_order_details?.length > 0) {
            setSelectedDieProfile(
              responsedData.work_order_details
                .map(detail => detail.die_profile?.id)
                .filter(Boolean),
            );
            setSelectedAlloy(
              responsedData.work_order_details
                .map(detail => detail.alloy?.id)
                .filter(Boolean),
            );
            setSelectedTemper(
              responsedData.work_order_details
                .map(detail => detail.temper?.id)
                .filter(Boolean),
            );

            // Initialize selectedJobWork, selectedSurfaceFinish, and selectedEngineeringOptions
            const jobWorkMap = {};
            const surfaceFinishMap = {};
            const engineeringOptionsMap = {};

            responsedData.work_order_details.forEach((detail, index) => {
             
              jobWorkMap[index] = detail.jobworks || [];

              // Set surface finish options
              const surfaceFinishArray = [];
              if (detail.anodising) surfaceFinishArray.push('anodising');
              if (detail.powder_coating)
                surfaceFinishArray.push('powder_coating');
              if (detail.pvdf) surfaceFinishArray.push('pvdf');
              surfaceFinishMap[index] = surfaceFinishArray;

              // Set engineering options
              const engineeringArray = [];
              if (detail.cutting) engineeringArray.push('cutting');
              if (detail.machining) engineeringArray.push('machining');
              if (detail.deburring) engineeringArray.push('deburring');
              engineeringOptionsMap[index] = engineeringArray;
            });



          }

          if (responsedData?.bill_to?.id === responsedData?.ship_to?.id) {
            setSameCustomer(true);
          } else {
            setSameCustomer(false);
          }
          if (responsedData?.workorder_type === 'Job Work') {
            message.success(
              'You can only edit the Bill To and Ship To details',
            );
          }
          const initialMap = responsedData.work_order_details.reduce(
            (acc, item, index) => {
              acc[index] = item.modify_nalco_rate;
              return acc;
            },
            {},
          );
          setInitialValues(initialMap);
        }
      } catch (error) {
        console.error('Error fetching work order:', error);
      }
    };
    fetchWorkOrder();
  }, [id, form, dispatch]);

  const validateFileList = () => {
    if (poCopyList.length === 0) {
      return Promise.reject(new Error('Please upload a file.'));
    }
    return Promise.resolve();
  };

  const getClassNameCustomer = value => {
    const selected = customerList.find(item => item.id === selectCustomer);
    return selected && selected.deleted === 1
      ? 'inactive-option'
      : 'active-option';
  };

  const getClassNameDieProfile = index => {
    if (!Array.isArray(selectedDieProfile) || !Array.isArray(dieProfileList)) {
      return 'active-option';
    }
    const value = selectedDieProfile[index] || '';
    if (!value) return 'active-option';
    const selected = dieProfileList.find(item => item.id === value);
    return selected && selected.deleted === 1
      ? 'inactive-option'
      : 'active-option';
  };

  const getClassNameAlloy = index => {
    const value = selectedAlloy[index];
    if (!value) return 'active-option';
    const selected = alloyList.find(item => item.id === value);
    return selected && selected.deleted === 1
      ? 'inactive-option'
      : 'active-option';
  };

  const getClassNameTemper = index => {
    const value = selectedTemper[index];
    if (!value) return 'active-option';
    const selected = temperList.find(item => item.id === value);
    return selected && selected.deleted === 1
      ? 'inactive-option'
      : 'active-option';
  };

  useEffect(() => {
    dispatch(
      fetchCustomerDropdown({
        page: 1,
        pagesize: 10000,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
    dispatch(
      fetchAlloyDropdown({
        page: 1,
        pagesize: 10000,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
    dispatch(
      fetchTemperDropdown({
        page: 1,
        pagesize: 10000,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
    dispatch(
      fetchDieProfileDropdown({
        page: 1,
        pagesize: 50000,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
    dispatch(fetchPackingModeList({ page: 1, pagesize: 100 }));
    dispatch(fetchUserList({ page: 1, pagesize: 1000 }));
     dispatch(fetchjobWorkList());
    dispatch(resetJobWorkList());
    dispatch(resetDieProfileList());
    dispatch(resetDieProfileData());
    dispatch(resetCurrentCustomerPage());
    dispatch(resetCurrentAlloyListPage());
    dispatch(resetTempersList());
    dispatch(resetCurrentTemperPage());
    dispatch(resetCustomerList());
    dispatch(resetAlloyList());
  }, [dispatch, page, pagesize, search, orderBy]);

  useEffect(() => {
    dispatch(fetchCustomerDropdown());
    dispatch(fetchAlloyDropdown());
    dispatch(fetchTemperDropdown());
    dispatch(resetCurrentDieProfilePage());
    dispatch(resetDieProfileList());
    dispatch(resetCustomerList());
    dispatch(resetCurrentCustomerPage());
    dispatch(resetAlloyList());
    dispatch(resetCurrentAlloyListPage());
    dispatch(resetTempersList());
    dispatch(resetCurrentTemperPage());
    dispatch(resetPackingModeData());
    dispatch(resetUserList());
  }, []);

  const debouncedSearchDieProfile = debounce((value, dispatch) => {
    dispatch(resetDieProfileData());
    dispatch(
      fetchDieProfileDropdown({
        page: 1,
        pagesize: 50,
        search: value || '',
      }),
    );
  }, 300);

  const handleSearchDieProfile = value => {
    setSearchDieProfile(value);
    if (value) {
      debouncedSearchDieProfile(value, dispatch);
    } else {
      dispatch(resetDieProfileData());
      dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 50 }));
    }
  };

  const onScrollDieProfile = event => {
    const target = event.target;
    if (
      !dieProfileLoading &&
      dieProfileHasMore &&
      target.scrollHeight - target.scrollTop - target.clientHeight < 50
    ) {
      const nextPage = dieProfileCurrentPage + 1;
      dispatch(
        fetchDieProfileDropdown({
          page: nextPage,
          pagesize: 50,
          search: searchDieProfile || '',
        }),
      );
    }
  };

  const handleAlloy = async (selected, option, index) => {
    console.log('Selected alloy:', selected, 'at index:', index);
    const updatedQuotationDetails = form
      .getFieldValue('work_order_details')
      .map((item, i) => (i === index ? { ...item, alloy: selected } : item));
    const customerId = form.getFieldValue('customer')
    //   for (let i = 0; i < updatedQuotationDetails.length; i++) {
    const dieId = updatedQuotationDetails[index]?.die_profile?.value ?? updatedQuotationDetails[index]?.die_profile;

    console.log("dieId", dieId);
    // const alloyId = updatedQuotationDetails[index]?.alloy;
    // const temperId = updatedQuotationDetails[index]?.temper;
    const alloyId = updatedQuotationDetails[index]?.alloy?.value ?? updatedQuotationDetails[index]?.alloy;
    const temperId = updatedQuotationDetails[index]?.temper?.value ?? updatedQuotationDetails[index]?.temper;
    console.log("Fetching conversion data for customer on Alloy Change:", "Company", customerId, "DieId", dieId, "alloyId:", alloyId, "temperId:", temperId, "at index:", index);
    if (customerId && dieId && alloyId && temperId) {
      await fetchConversionData(customerId, dieId, alloyId, temperId, index);
    } else {
      updatedQuotationDetails[index] = {
        ...updatedQuotationDetails[index],
        conversion: '',

      };
      console.log("Alloy Temp Change UpdatedQuotationDetails", updatedQuotationDetails)
      form.setFieldsValue({ work_order_details: updatedQuotationDetails });
      // }
    }
    // form.setFieldsValue({ work_order_details: updatedDetails });


  };

  const handleSearchAlloy = value => {
    setSearchAlloy(value);
    if (value) {
      dispatch(resetAlloyList());
      dispatch(fetchAlloyDropdown({ page: 1, pagesize: 1000, search: value }));
    } else {
      setSearchAlloy('');
      dispatch(resetAlloyList());
      dispatch(resetCurrentAlloyListPage());
      dispatch(fetchAlloyDropdown({ page: 1, pagesize: 1000 }));
    }
  };

  const onScrollAlloy = async event => {
    const target = event.target;
    if (
      !alloyLoading &&
      alloyHasMore &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      dispatch(fetchAlloyDropdown({ page: alloyCurrentPage, pagesize: 1000 }));
    }
  };

  const [isRemarkRequired, setIsRemarkRequired] = useState(false);
  const [pricePerKg, setPricePerKg] = useState('');

  const handleNalcoChange = value => {
    setIsRemarkRequired(value && parseFloat(value) > 0);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await dispatch(
          fetchNalcoRateForQuotation({ page: 1, pagesize: 10 }),
        );
        if (response) {
          setPricePerKg(response.payload.data.rate);
        } else {
          console.error('Unexpected response structure:', response);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [dispatch]);

  const handleSearchTemer = value => {
    setSearchTemper(value);
    if (value) {
      dispatch(resetTempersList());
      dispatch(fetchTemperDropdown({ page: 1, pagesize: 1000, search: value }));
    } else {
      setSearchTemper('');
      dispatch(resetTempersList());
      dispatch(resetCurrentTemperPage());
      dispatch(fetchTemperDropdown({ page: 1, pagesize: 1000 }));
    }
  };

  const handleBillToChange = value => {
    setSelectCustomer(value);
    form.setFieldValue('bill_to', value);
    form.validateFields(['bill_to']);
    handleChangeCustomer(value);
    if (value === form.getFieldValue('ship_to')) {
      setSameCustomer(true);
    } else {
      setSameCustomer(false);
    }
  };

  const handleShipToChange = value => {
    form.setFieldValue('ship_to', value);
    form.validateFields(['ship_to']);
    handleChangeCustomer(value);
    if (value === form.getFieldValue('bill_to')) {
      setSameCustomer(true);
    } else {
      setSameCustomer(false);
    }
  };

  const onScrollTemper = async event => {
    const target = event.target;
    if (
      !temperLoading &&
      temperHasMore &&
      target.scrollTop + target.offsetHeight === target.scrollHeight
    ) {
      dispatch(fetchTemperDropdown({ page: alloyCurrentPage, pagesize: 1000 }));
    }
  };

  const handleSearchCustomer = value => {
    setSearchCustomer(value);
    if (value) {
      dispatch(fetchCustomerDropdown({ page: 1, pagesize: 1000, search: value }));
    } else {
      setSearchCustomer('');
      dispatch(fetchCustomerDropdown({ page: 1, pagesize: 1000 }));
    }
  };

  const handleChangeCustomer = value => {
    setSearchCustomer(value);
  };

  useEffect(() => {
    if (selectedIndex === null) return;
    const workOrderDetails = form.getFieldValue('work_order_details') || [];
    const updatedDetails = [...workOrderDetails];
    const item = updatedDetails[selectedIndex];
    if (item) {
      const currentKg = currentWTKg || 0;
      const length = parseFloat(item.length) || 0;
      const pieces = parseFloat(item.pieces) || 0;
      const netWeight = ((currentKg * length) / 1000) * pieces;
      const toleranceValue = form.getFieldValue('tolerance') || '';
      const tolerancePercent = toleranceValue
        ? parseFloat(toleranceValue.replace(/[^\d.]/g, '')) || 0
        : 0;
      const isPositive = toleranceValue.includes('+');
      const isNegative = toleranceValue.includes('-');
      let max_weight = netWeight;
      let min_weight = netWeight;
      if (tolerancePercent > 0) {
        if (isPositive && isNegative) {
          max_weight = netWeight * (1 + tolerancePercent / 100);
          min_weight = netWeight * (1 - tolerancePercent / 100);
        } else if (isPositive) {
          max_weight = netWeight * (1 + tolerancePercent / 100);
          min_weight = netWeight;
        } else if (isNegative) {
          min_weight = netWeight * (1 - tolerancePercent / 100);
          max_weight = netWeight;
        }
      }
      updatedDetails[selectedIndex] = {
        ...item,
        net_weight: netWeight.toFixed(2),
        max_weight: max_weight.toFixed(3),
        min_weight: min_weight.toFixed(3),
      };
      form.setFieldsValue({
        work_order_details: updatedDetails,
      });
    }
  }, [currentWTKg, selectedIndex, form]);

  // const handleFieldChange = (index, changedField, value) => {
  //   const workOrderDetails = form.getFieldValue('work_order_details') || [];
  //   const row = { ...workOrderDetails[index] } || {};
  //   const currentKg = row?.wt_kg_p_mt || currentWTKg || 0;
  //   const length = parseFloat(row.length) || 0;
  //   const pieces = parseFloat(row.pieces) || 0;
  //   const net_weight = parseFloat(row.net_weight) || 0;
  //   let calculatedNetWeight = net_weight;
  //   let calculatedPieces = pieces;
  //   if (changedField === 'net_weight') {
  //     if (net_weight > 0 && length > 0 && currentKg > 0) {
  //       calculatedPieces = net_weight / (currentKg * (length / 1000));
  //     }
  //   }
  //   if (changedField === 'pieces') {
  //     if (pieces > 0 && length > 0 && currentKg > 0) {
  //       calculatedNetWeight = ((currentKg * length) / 1000) * pieces;
  //       calculatedNetWeight = parseFloat(calculatedNetWeight.toFixed(3));
  //     }
  //   }
  //   if (changedField === 'length') {
  //     if (net_weight > 0 && length > 0 && currentKg > 0) {
  //       calculatedPieces = net_weight / (currentKg * (length / 1000));
  //       calculatedNetWeight =
  //         ((currentKg * length) / 1000) *
  //         parseFloat(calculatedPieces.toFixed(0));
  //       calculatedNetWeight = parseFloat(calculatedNetWeight.toFixed(3));
  //     }
  //   }
  //   const toleranceValue = form.getFieldValue('tolerance') || '';
  //   const tolerancePercent = toleranceValue
  //     ? parseFloat(toleranceValue.replace(/[^\d.]/g, '')) || 0
  //     : 0;
  //   const isPositive = toleranceValue.includes('+');
  //   const isNegative = toleranceValue.includes('-');
  //   let max_weight = calculatedNetWeight;
  //   let min_weight = calculatedNetWeight;
  //   if (tolerancePercent > 0) {
  //     if (isPositive && isNegative) {
  //       max_weight = calculatedNetWeight * (1 + tolerancePercent / 100);
  //       min_weight = calculatedNetWeight * (1 - tolerancePercent / 100);
  //     } else if (isPositive) {
  //       max_weight = calculatedNetWeight * (1 + tolerancePercent / 100);
  //       min_weight = calculatedNetWeight;
  //     } else if (isNegative) {
  //       min_weight = calculatedNetWeight * (1 - tolerancePercent / 100);
  //       max_weight = calculatedNetWeight;
  //     }
  //   }
  //   max_weight = parseFloat(max_weight.toFixed(3));
  //   min_weight = parseFloat(min_weight.toFixed(3));
  //   const updatedWorkOrderDetails = [...workOrderDetails];
  //   const updatedRow = {
  //     ...row,
  //     net_weight: calculatedNetWeight,
  //     wt_kg_p_mt: currentKg,
  //     max_weight: max_weight,
  //     min_weight: min_weight,
  //   };
  //   if (changedField === 'net_weight' || changedField === 'length') {
  //     updatedRow.pieces = Math.round(calculatedPieces);
  //   }
  //   updatedWorkOrderDetails[index] = updatedRow;
  //   form.setFieldsValue({
  //     work_order_details: updatedWorkOrderDetails,
  //   });
  // };

  const handleFieldChange = (index, changedField, rawValue) => {
    const workOrderDetails = form.getFieldValue('work_order_details') || [];
    const row = { ...workOrderDetails[index] };

    const currentKg = row?.wt_kg_p_mt || currentWTKg || 0;
    const length = Number(row.length) || 0;
    const pieces = Number(row.pieces) || 0;

    const netWeightStr =
      changedField === 'net_weight' ? rawValue : row.net_weight;

    const net_weight =
      netWeightStr !== '' && !isNaN(netWeightStr)
        ? Number(netWeightStr)
        : 0;

    let calculatedNetWeight = net_weight;
    let calculatedPieces = pieces;

    if (changedField === 'net_weight' && net_weight > 0 && length > 0 && currentKg > 0) {
      calculatedPieces = net_weight / (currentKg * (length / 1000));
    }

    if (changedField === 'pieces' && pieces > 0 && length > 0 && currentKg > 0) {
      calculatedNetWeight = ((currentKg * length) / 1000) * pieces;
    }

    if (changedField === 'length' && net_weight > 0 && length > 0 && currentKg > 0) {
      calculatedPieces = net_weight / (currentKg * (length / 1000));
      calculatedNetWeight = ((currentKg * length) / 1000) * calculatedPieces;

    }

    const toleranceValue = form.getFieldValue('tolerance') || '';
    const tolerancePercent = Number(toleranceValue.replace(/[^\d.]/g, '')) || 0;

    let max_weight = calculatedNetWeight;
    let min_weight = calculatedNetWeight;

    if (tolerancePercent > 0) {
      if (toleranceValue.includes('+') && toleranceValue.includes('-')) {
        max_weight *= 1 + tolerancePercent / 100;
        min_weight *= 1 - tolerancePercent / 100;
      } else if (toleranceValue.includes('+')) {
        max_weight *= 1 + tolerancePercent / 100;
      } else if (toleranceValue.includes('-')) {
        min_weight *= 1 - tolerancePercent / 100;
      }
    }

    const updatedRow = {
      ...row,
      net_weight: changedField === 'net_weight' ? netWeightStr : Number(calculatedNetWeight).toFixed(3),
      max_weight: Number(max_weight).toFixed(3),
      min_weight: Number(min_weight).toFixed(3),
      wt_kg_p_mt: currentKg,
      ...(changedField !== 'pieces' && { pieces: Math.round(calculatedPieces) }),
    };

    workOrderDetails[index] = updatedRow;

    form.setFieldsValue({
      work_order_details: workOrderDetails,
    });
  };

  const handleTemer = async (selected, option, index) => {
    console.log('Selected temper:', selected, 'at index:', index);
    const updatedQuotationDetails = form
      .getFieldValue('work_order_details')
      .map((item, i) => (i === index ? { ...item, temper: selected } : item));
    // form.setFieldsValue({ work_order_details: updatedDetails });
    const customerId = form.getFieldValue('customer')
    //   for (let i = 0; i < updatedQuotationDetails.length; i++) {
    // const dieId = updatedQuotationDetails[index]?.die_profile;
    const dieId = updatedQuotationDetails[index]?.die_profile?.value ?? updatedQuotationDetails[index]?.die_profile;

    console.log("dieId", dieId)
    const alloyId = updatedQuotationDetails[index]?.alloy?.value ?? updatedQuotationDetails[index]?.alloy;
    const temperId = updatedQuotationDetails[index]?.temper?.value ?? updatedQuotationDetails[index]?.temper;
    console.log("Fetching conversion data for customer on Alloy And Temper Change:", "Company", customerId, "DieId", dieId, "alloyId:", alloyId, "temperId:", temperId, "at index:", index);
    if (customerId && dieId && alloyId && temperId) {
      await fetchConversionData(customerId, dieId, alloyId, temperId, index);
    } else {
      updatedQuotationDetails[index] = {
        ...updatedQuotationDetails[index],
        conversion: '',

      };
      console.log("Alloy Temp Change UpdatedQuotationDetails", updatedQuotationDetails)
      form.setFieldsValue({ work_order_details: updatedQuotationDetails });
      // }
    }

  };



  const handleFinish = values => {
    setBtnLoading(true);
    const formattedOrderDate = dayjs(values.order_date).format('YYYY-MM-DD');
    const formattedDeliveryDate = dayjs(values.delivery_date).format(
      'YYYY-MM-DD',
    );
    const formattedPurchaseOrderDate = dayjs(values.purchase_order_date).format(
      'YYYY-MM-DD',
    );

    const basePayload = {
      bill_to: values.bill_to,
      ship_to: values.ship_to,
      order_date: formattedOrderDate,
      delivery_date: formattedDeliveryDate,
      purchase_order_no: values.purchase_order_no,
      purchase_order_date: formattedPurchaseOrderDate,
      packing_mode: values.packing_mode,
      project_name: values.project_name,
      nalco_type: values.nalco_type,
      approved_by: values?.approved_by || null,
      nalco_rate: values.nalco_rate,
      tolerance: values.tolerance,
      remarks: values.remarks,
      status: values.status,
      order_no: values.order_no,
      work_order_details: values.work_order_details?.map(detail => {
        const formattedDetail = {
          die_profile: detail?.die_profile?.value || detail?.die_profile,
          description: detail?.description || '',
          id: detail?.id || null,
          alloy:
            typeof detail?.alloy === 'object'
              ? detail?.alloy?.value
              : detail?.alloy,
          temper:
            typeof detail?.temper === 'object'
              ? detail?.temper?.value
              : detail?.temper,
          customer_reference_number: detail.customer_reference_number,
          pieces: parseFloat(detail.pieces) || null,
          net_weight: parseFloat(detail.net_weight) || null,
          min_weight: parseFloat(detail.min_weight) || null,
          max_weight: parseFloat(detail.max_weight) || null,
          length: parseFloat(detail.length) || null,
          nalco_rate: parseFloat(detail.nalco_rate) || null,
          die_over_weight: detail.die_over_weight || false,
          nalco_rate_change_reason: detail.nalco_rate_change_reason,
          modify_nalco_rate: detail.modify_nalco_rate || false,
          packing_charge: parseFloat(detail.packing_charge) || null,
          conversion: parseFloat(detail.conversion) || null,
          jobworks: Array.isArray(detail.jobworks) ? detail.jobworks : [],
          cutting: detail?.cutting || false,
          machining: detail?.machining || false,
          deburring: detail?.deburring || false,
          anodising: detail?.anodising || false,
          powder_coating: detail?.powder_coating || false,
          pvdf: detail?.pvdf || false,
          out_source: detail?.out_source,
          anodising_description: detail.anodising_description || '',
          anodising_price: parseFloat(detail.anodising_price) || null,
          powder_coating_description: detail.powder_coating_description || '',
          powder_coating_price: parseFloat(detail.powder_coating_price) || null,
          pvdf_description: detail.pvdf_description || '',
          pvdf_price: parseFloat(detail.pvdf_price) || null,
          laser_marking_description: detail.laser_marking_description || '',
          laser_marking_price: parseFloat(detail.laser_marking_price) || null,
          cutting_price: parseFloat(detail.cutting_price) || null,
          machining_price: parseFloat(detail.machining_price) || null,
          deburring_price: parseFloat(detail.deburring_price) || null,
        };
        Object.keys(formattedDetail).forEach(key => {
          if (formattedDetail[key] === null) delete formattedDetail[key];
        });
        return formattedDetail;
      }),
      workorder_type: values.workorder_type,
    };
    const formData = new FormData();
    formData.append('data', JSON.stringify(basePayload));
    if (poCopyList && poCopyList.length > 0 && poCopyList[0].originFileObj) {
      formData.append('po_copy', poCopyList[0].originFileObj);
    }
    dispatch(updateWorkOrder({ id, values: formData }))
      .unwrap()
      .then(result => {
        if (result.success) {
          message.success('Work Order Updated Successfully!');
          if (result?.skipped_updates_count > 0) {
            const msgs = result?.skipped_updates;

            if (Array.isArray(msgs) && msgs.length > 0) {
              const errorMessage = msgs.join("\n"); // combine into one message

              message.error({
                content: (
                  <div style={{ whiteSpace: "pre-line" }}>
                    {errorMessage}
                  </div>
                ),
                duration: 4,
              });
            }
          }
          navigate(-1);
        }
      })
      .catch(error => {
        let displayMessage = '';
        if (typeof error === 'object') {
          displayMessage = Object.entries(error)
            .map(([key, value]) => `${key}: ${value.join(', ')}`)
            .join('\n');
          message.error(displayMessage);
        } else {
          message.error(
            `Error: ${error || 'Failed to update the work order.'}`,
          );
        }
      })
      .finally(() => {
        setBtnLoading(false);
      });
  };

  const handleSameCustomerCheck = e => {
    const checked = e.target.checked;
    setSameCustomer(checked);
    if (checked) {
      const selectedCustomer = form.getFieldValue('bill_to');
      form.setFieldsValue({
        ship_to: selectedCustomer,
      });
    } else {
      form.setFieldsValue({
        ship_to: null,
      });
    }
  };

  const handleToleranceChange = value => {
    const workOrderDetails = form.getFieldValue('work_order_details') || [];
    const toleranceMatch = value.match(/(\+-|\+|-)?\d+/g);
    const toleranceValue = toleranceMatch
      ? Math.abs(parseFloat(toleranceMatch[0].replace('+-', '')))
      : 0;
    const hasPositive = toleranceMatch.some(val => val.includes('+')) && !value.includes('+-');
    const hasNegative = toleranceMatch.some(val => val.includes('-')) && !value.includes('+-');
    const hasBoth = value.includes('+-') || value.includes('卤');
    const updatedDetails = workOrderDetails.map(item => {
      const netWeight = parseFloat(item.net_weight) || 0;
      let max_weight = netWeight;
      let min_weight = netWeight;
      if (toleranceValue > 0) {
        if (hasBoth) {
          max_weight = netWeight * (1 + toleranceValue / 100);
          min_weight = netWeight * (1 - toleranceValue / 100);
        } else if (hasPositive) {
          max_weight = netWeight * (1 + toleranceValue / 100);
          min_weight = netWeight;
        } else if (hasNegative) {
          min_weight = netWeight * (1 - toleranceValue / 100);
          max_weight = netWeight;
        }
      }
      return {
        ...item,
        net_weight: netWeight.toFixed(3),
        max_weight: max_weight.toFixed(3),
        min_weight: min_weight.toFixed(3),
      };
    });
    form.setFieldsValue({ work_order_details: updatedDetails });
  };

  const handleCompanyNameChange = async value => {
    form.setFieldValue('customer', value);
    setSameCustomer(true);
    form.setFieldsValue({
      ship_to: value,
    });
    form.validateFields(['customer']);
    handleChangeCustomer(value);
    const quotationDetails = form.getFieldValue('work_order_details') || [];
    const updatedQuotationDetails = [...quotationDetails];
    updatedQuotationDetails.forEach((item, index) => {
      updatedQuotationDetails[index] = {
        ...item,
        conversion: '',
      };
    });
    form.setFieldsValue({ work_order_details: updatedQuotationDetails });

    for (let i = 0; i < updatedQuotationDetails.length; i++) {
      const dieId = updatedQuotationDetails[i]?.die_profile?.value ?? updatedQuotationDetails[i]?.die_profile;
      const alloyId = updatedQuotationDetails[i]?.alloy?.value ?? updatedQuotationDetails[i]?.alloy;
      const temperId = updatedQuotationDetails[i]?.temper?.value ?? updatedQuotationDetails[i]?.temper;
      console.log("Fetching conversion data for customer on Company Change:", value, "dieId:", dieId, "alloyId:", alloyId, "temperId:", temperId, "at index:", i);
      if (value && dieId && alloyId && temperId) {
        await fetchConversionData(value, dieId, alloyId, temperId, i);
      }

    }
  };

  const fetchConversionData = async (customerId, dieId, alloyId, temperId, index) => {
    const updatedQuotationDetails = [
      ...form.getFieldValue('work_order_details'),
    ];
    try {
      const response = await dispatch(
        fetchCustomerConversionValue({
          page: 1,
          pagesize: 10,
          customer_id: customerId,
          die_id: dieId,
          alloy_id: alloyId,
          temper_id: temperId,
        }),
      );
      console.log("Conversion API Response for customerId:", customerId, "dieId:", dieId, "alloyId:", alloyId, "temperId:", temperId, "at index:", index);
      if (response && response.payload.data.length > 0) {
        const conversionValue = response.payload.data[0].conversion;
        updatedQuotationDetails[index] = {
          ...updatedQuotationDetails[index],
          conversion: conversionValue,
        };
      } else {
        updatedQuotationDetails[index] = {
          ...updatedQuotationDetails[index],
          conversion: null,
        };
      }
      form.setFieldsValue({ work_order_details: updatedQuotationDetails });
    } catch (error) {
      console.error('Error fetching conversion data:', error);
      updatedQuotationDetails[index] = {
        ...updatedQuotationDetails[index],
        conversion: null,
      };
      form.setFieldsValue({ work_order_details: updatedQuotationDetails });
    }
  };

  const handleDieProfileChange = async (value, index) => {
    console.log('Selected die profile:', value?.value, 'at index:', index);
    const updatedQuotationDetails = [
      ...form.getFieldValue('work_order_details'),
    ];
    updatedQuotationDetails[index] = {
      ...updatedQuotationDetails[index],
      die_profile: value?.value
    };
    form.setFieldsValue({ work_order_details: updatedQuotationDetails });
    setSelectedIndex(index);
    if (value) {
      try {
        const response = await dispatch(
          fetchDieProfileById(value?.value),
        ).unwrap();
        if (response?.results?.data?.length > 0) {
          const customerRefNo = response.results.data[0]?.customer_reference_number || '';
          const currentWTKg = response.results.data[0]?.wt_kg_p_mt || '';
          setCurrentWTKg(currentWTKg);
          updatedQuotationDetails[index] = {
            ...updatedQuotationDetails[index],
            customer_reference_number: customerRefNo,
            wt_kg_p_mt: currentWTKg,
          };
          const row = updatedQuotationDetails[index];
          const length = parseFloat(row.length) || 0;
          const pieces = parseFloat(row.pieces) || 0;
          if (length > 0 && pieces > 0 && currentWTKg > 0) {
            const net_weight = (length * currentWTKg * pieces) / 1000;
            const toleranceValue = form.getFieldValue('tolerance') || '';
            const tolerancePercent = toleranceValue
              ? parseFloat(toleranceValue.replace(/[^\d.]/g, '')) || 0
              : 0;
            const isPositive = toleranceValue.includes('+');
            const isNegative = toleranceValue.includes('-');
            let max_weight = net_weight;
            let min_weight = net_weight;
            if (tolerancePercent > 0) {
              if (isPositive && isNegative) {
                max_weight = net_weight * (1 + tolerancePercent / 100);
                min_weight = net_weight * (1 - tolerancePercent / 100);
              } else if (isPositive) {
                max_weight = net_weight * (1 + tolerancePercent / 100);
                min_weight = net_weight;
              } else if (isNegative) {
                min_weight = net_weight * (1 - tolerancePercent / 100);
                max_weight = net_weight;
              }
            }
            updatedQuotationDetails[index] = {
              ...row,
              net_weight: Number(net_weight).toFixed(3),
              max_weight: Number(max_weight).toFixed(3),
              min_weight: Number(min_weight).toFixed(3),
              wt_kg_p_mt: currentWTKg,
            };
          }
          form.setFieldsValue({
            work_order_details: updatedQuotationDetails,
            customer_reference_number: customerRefNo,
          });
          const customerId = form.getFieldValue('customer');
          const alloyId = updatedQuotationDetails[index]?.alloy;
          const temperId = updatedQuotationDetails[index]?.temper;
          console.log("Fetching conversion data for customer on Die Profile Change:", "Company", customerId, "DieId", value, "alloyId:", alloyId, "temperId:", temperId, "at index:", index);
          if (customerId && value?.value && alloyId && temperId) {
            await fetchConversionData(customerId, value?.value, alloyId, temperId, index);
          }
        }
      } catch (error) {
        console.error('Error fetching customer reference number:', error);
      }
    } else {
      updatedQuotationDetails[index] = {
        ...updatedQuotationDetails[index],
        die_profile: value,
        conversion: '',
        customer_reference_number: "",
      };
      form.setFieldsValue({ work_order_details: updatedQuotationDetails });
    }
  };

  const [fileList, setFileList] = useState([]);
  const [uploadDiaDiagramError, setUploadDiaDiagramError] = useState('');

  const getFileType = fileName => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'jpeg':
      case 'jpg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'dwg':
        return 'dwg';
      default:
        return false;
    }
  };

  const [
    uploadCustApprovedDieDiageamError,
    setUploadCustApprovedDieDiageamError,
  ] = useState('');

  const handleFileChange = info => {
    const newFileList = info.fileList.slice(-1);
    const file = newFileList[0]?.originFileObj;
    if (file) {
      if (file.size / 1024 / 1024 > 2) {
        setUploadCustApprovedDieDiageamError(
          'File size must be smaller than 2MB!',
        );
        setPoCopyList([]);
      } else {
        setUploadCustApprovedDieDiageamError('');
        setPoCopyList(newFileList);
      }
    } else {
      setUploadCustApprovedDieDiageamError('');
      setPoCopyList([]);
    }
  };

  const [searchTemper, setSearchTemper] = useState('');
  const workOrderType = Form.useWatch('workorder_type', form);
  return (
    <>
      <div className="sticyaction">
        <div className="pagenamerow">
          <h2>Edit Work Order</h2>
          <div className="bredcrumbwrp">
            <Link className="back" onClick={() => { navigate(-1); }}>
              <Back /> BACK
            </Link>
            <Breadcrumb
              items={[
                { title: <Link to="/dashboard">Home</Link> },
                { title: <Link to="/work-order-list">Work Order List</Link> },
                { title: 'Edit Work Order' },
              ]}
            />
          </div>
        </div>
      </div>
      <div className="formwrapper">
        <Form layout="vertical" form={form} onFinish={handleFinish}>
          <div className="formrowsection">
            <Row gutter={16}>
              <Col xs={24} sm={12} xl={8} xxl={6}>
                <Form.Item
                  name="bill_to"
                  label="Customer (Bill To)"
                  rules={[
                    { required: true, message: 'Please select company name' },
                  ]}
                >
                  <AutoOpenSelect
                    placeholder="Please Select Company Name"
                    onChange={value => {
                      handleBillToChange(value);
                      handleCompanyNameChange(value);
                    }}
                    onSearch={handleSearchCustomer}
                    className={getClassNameCustomer()}
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    showSearch={true}
                    value={searchCustomer}
                    allowClear
                  >
                    {[
                      ...new Map(
                        customerList.map(item => [item.id, item]),
                      ).values(),
                    ].map((el, index) => (
                      <Option
                        key={el.id}
                        value={el.id}
                        disabled={el.deleted === 1}
                      >
                        {el?.customer_name}
                      </Option>
                    ))}
                  </AutoOpenSelect>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8} xxl={6}>
                <label
                  className="customlabel flex"
                  style={{ marginBottom: '3px' }}
                >
                  {' '}
                  Customer (Ship To)
                  <div className="ml-2">
                    <Checkbox
                      checked={sameCustomer}
                      onChange={handleSameCustomerCheck}
                    >
                      Same as Bill To
                    </Checkbox>
                  </div>
                </label>
                <Form.Item name="ship_to" label="">
                  <AutoOpenSelect
                    placeholder="Please Select Company Name"
                    onChange={value => {
                      handleShipToChange(value);
                    }}
                    className={getClassNameCustomer()}
                    onSearch={handleSearchCustomer}
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    showSearch={true}
                    value={searchCustomer}
                    allowClear
                  >
                    {[
                      ...new Map(
                        customerList.map(item => [item.id, item]),
                      ).values(),
                    ].map((el, index) => (
                      <Option
                        key={el.id}
                        value={el.id}
                        disabled={el.deleted === 1}
                      >
                        {el?.customer_name}
                      </Option>
                    ))}
                  </AutoOpenSelect>
                </Form.Item>
              </Col>
              <Col xs={12} sm={6} xl={4}>
                <Form.Item
                  name="order_date"
                  label="Order Date"
                  rules={[
                    { required: true, message: 'Please select order date' },
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD-MM-YYYY"
                    defaultValue={moment()}
                    disabledDate={current =>
                      current && current.isAfter(dayjs(), 'day')
                    }
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6} xl={4}>
                <Form.Item
                  name="delivery_date"
                  label={
                    <>
                      Delivery Date
                      <Tooltip title="Delivery date should be at least 12 days after the order date">
                        <InfoCircleOutlined
                          style={{ marginLeft: 5, color: '#1890ff' }}
                        />
                      </Tooltip>
                    </>
                  }
                  rules={[
                    { required: true, message: 'Please select delivery date' },
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD-MM-YYYY"
                    disabledDate={disablePastDates}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8} xxl={4}>
                <Form.Item
                  name="purchase_order_no"
                  label="Purchase Order No"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter valid purchase order no',
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter Purchase Order No"
                    disabled={workOrderType === 'Job Work'}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8} xxl={4}>
                <Form.Item
                  name="purchase_order_date"
                  label="Purchase Order Date"
                  rules={[
                    {
                      required: true,
                      message: 'Please select purchase order date',
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD-MM-YYYY"
                    disabledDate={current =>
                      current && current.isAfter(dayjs(), 'day')
                    }
                    disabled={workOrderType === 'Job Work'}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8} xxl={6}>
                <Form.Item
                  name="packing_mode"
                  label="Mode Of Packing"
                  rules={[
                    { required: true, message: 'Please select mode of packing' },
                  ]}
                >
                  <AutoOpenSelect
                    placeholder="Select Mode Of Packing"
                    options={Array.from(
                      new Map(
                        packingModeList.map(item => [item.id, { label: item.name, value: item.id }])
                      ).values()
                    )}
                    mode="multiple"
                    loading={packingModeLoading}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8} xxl={6}>
                <Form.Item
                  name="project_name"
                  label="Project Name"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter valid project name',
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter Project Name"
                    disabled={workOrderType === 'Job Work'}
                    onChange={e => {
                      const value = e.target.value;
                      const formattedValue = value
                        .toLowerCase()
                        .replace(/\b\w/g, char => char.toUpperCase());
                      form.setFieldsValue({ project_name: formattedValue });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={8} xxl={4}>
                <Form.Item
                  name="tolerance"
                  label="Tolerance"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a tolerance value',
                    },
                  ]}
                >
                  <AutoOpenSelect
                    placeholder="Select Tolerance"
                    onChange={handleToleranceChange}
                  >
                    {TOLERANCE_OPTIONS.map(opt => (
                      <Option
                        key={opt.value}
                        value={opt.value}
                        style={opt.label.startsWith('±') ? { letterSpacing: '2px' } : undefined}
                      >
                        {opt.label}
                      </Option>
                    ))}
                  </AutoOpenSelect>
                </Form.Item>
              </Col>
              <Col xs={12} sm={12} xl={8} xxl={4}>
                <Form.Item
                  name="nalco_type"
                  label="Nalco Rate"
                  rules={[
                    { required: true, message: 'Please select nalco type' },
                  ]}
                >
                  <Radio.Group>
                    <Radio value="Fixed">Fixed</Radio>
                    <Radio value="Variable">Variable</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
               <Col xs={12} sm={12} xl={8} xxl={4}>
                <Form.Item shouldUpdate={(prev, curr) => prev.nalco_type !== curr.nalco_type}>
                  {({ getFieldValue }) =>
                    getFieldValue("nalco_type") === "Fixed" ? (
                     
                      <Form.Item
                        name="approved_by"
                        label="Approved By"
                        rules={[{ required: true, message: "Please select user" }]}
                      >
                        <Select placeholder="Select user">
                          {userNameList?.map((user) => (
                            <Select.Option key={user.id} value={user.id}>
                              {user?.first_name} {user?.last_name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    
                    ) : null
                  }
                </Form.Item>
                </Col>
              <Col xs={12} sm={12} xl={8} xxl={4}>
                <Form.Item
                  name="workorder_type"
                  label="Work Order Type"
                  rules={[
                    { required: true, message: 'Please select work order type' },
                  ]}
                >
                  <Radio.Group disabled>
                    <Radio value="In House">In House</Radio>
                    <Radio value="Job Work">Job Work</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} lg={12} xxl={12}>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea
                    placeholder="Enter remarks"
                    disabled={workOrderType === 'Job Work'}
                    onChange={e => {
                      const value = e.target.value;
                      const formattedValue =
                        value.trim() === ''
                          ? null
                          : value
                            .toLowerCase()
                            .replace(/\b\w/g, char => char.toUpperCase());
                      form.setFieldsValue({ remarks: formattedValue });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item
                  name="po_copy"
                  label="PO Copy"
                  valuePropName="fileList"
                  rules={[
                    {
                      validator: validateFileList,
                    },
                  ]}
                >
                  <div>
                    <Upload
                      beforeUpload={() => false}
                      listType="picture"
                      accept=".pdf,.jpeg,.jpg,.png"
                      maxCount={1}
                      onChange={handleFileChange}
                      disabled={workOrderType === 'Job Work'}
                      fileList={poCopyList}
                      showUploadList={{
                        showPreviewIcon: true,
                        showRemoveIcon: true,
                      }}
                    >
                      {poCopyList.length < 1 && (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            backgroundColor: '#12406a',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: '14px',
                            gap: '8px',
                          }}
                        >
                          <PlusOutlined />
                          <span>Upload</span>
                        </div>
                      )}
                    </Upload>
                  </div>
                  {uploadDiaDiagramError && (
                    <div style={{ color: 'red', marginTop: 5 }}>
                      {uploadDiaDiagramError}
                    </div>
                  )}
                  <label className="filenote">
                    Upload PDF, JPEG, JPG or PNG file
                  </label>
                </Form.Item>
              </Col>
            </Row>
          </div>
          {/* {workOrderType !== 'Job Work' && ( */}
          <div className="formrowsection" hidden={workOrderType === 'Job Work'}>
            <h3 className="rowsubtitle">Add New Item</h3>
            <Form.List name="work_order_details">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <>
                      <ExcessStockCount
                        profileId={
                          form.getFieldValue([
                            'work_order_details',
                            name,
                            'die_profile',
                          ])?.value
                            ? form.getFieldValue([
                              'work_order_details',
                              name,
                              'die_profile',
                            ])?.value
                            : form.getFieldValue([
                              'work_order_details',
                              name,
                              'die_profile',
                            ])
                        }
                        length={form.getFieldValue([
                          'work_order_details',
                          name,
                          'length',
                        ])}
                        alloyId={
                          form.getFieldValue([
                            'work_order_details',
                            name,
                            'alloy',
                          ])?.value
                            ? form.getFieldValue([
                              'work_order_details',
                              name,
                              'alloy',
                            ])?.value
                            : form.getFieldValue([
                              'work_order_details',
                              name,
                              'alloy',
                            ])
                        }
                        temperId={
                          form.getFieldValue([
                            'work_order_details',
                            name,
                            'temper',
                          ])?.value
                            ? form.getFieldValue([
                              'work_order_details',
                              name,
                              'temper',
                            ])?.value
                            : form.getFieldValue([
                              'work_order_details',
                              name,
                              'temper',
                            ])
                        }
                      />
                      <Row
                        gutter={[24, 24]}
                        className="rowItem mx-0"
                        align="middle"
                        key={key}
                        wrap={false}
                        style={{ width: '100%' }}
                      >
                        <Col flex="auto">
                          <Row gutter={16} key={key}>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'die_profile']}
                                label="Profile Number"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select profile number',
                                  },
                                ]}
                              >
                                <AutoOpenSelect
                                  placeholder="Select Profile Number"
                                  onChange={value =>
                                    handleDieProfileChange(value, name)
                                  }
                                  value={
                                    form.getFieldValue([
                                      'work_order_details',
                                      name,
                                      'die_profile',
                                    ])
                                      ? {
                                        value: form.getFieldValue([
                                          'work_order_details',
                                          name,
                                          'die_profile',
                                        ]),
                                        label: '',
                                      }
                                      : undefined
                                  }
                                  labelInValue
                                  loading={dieProfileLoading}
                                  className={getClassNameDieProfile(name)}
                                  notFoundContent={null}
                                  open={forceOpen || undefined}
                                  onPopupScroll={onScrollDieProfile}
                                  showSearch={true}
                                  filterOption={(input, option) =>
                                    option.children
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  onSearch={handleSearchDieProfile}
                                  allowClear
                                  onClear={() => {
                                    dispatch(resetDieProfileData());
                                    dispatch(
                                      fetchDieProfileDropdown({
                                        page: 1,
                                        pagesize: 100,
                                      }),
                                    );
                                  }}
                                  dropdownRender={menu => (
                                    <>
                                      {showDropdownLoader ? (
                                        <div
                                          style={{
                                            padding: '16px',
                                            textAlign: 'center',
                                          }}
                                        >
                                          <Spin size="small" />
                                        </div>
                                      ) : (
                                        menu
                                      )}
                                    </>
                                  )}
                                >
                                  {showDropdownLoader ? (
                                    <Option key="loading" disabled>
                                      Loading...
                                    </Option>
                                  ) : (
                                    [
                                      ...new Map(
                                        dieProfileList.map(item => [
                                          item.id,
                                          item,
                                        ]),
                                      ).values(),
                                    ].map((el, index) => (
                                      <Option
                                        key={el.id}
                                        value={el.id}
                                        data-weight={el.wt_kg_p_mt}
                                        disabled={el.deleted === 1}
                                      >
                                        {el.die_number}
                                      </Option>
                                    ))
                                  )}
                                </AutoOpenSelect>
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                name={[name, 'customer_reference_number']}
                                label="Customer Reference No."
                              >
                                <Input
                                  placeholder="Enter Customer Ref. No"
                                  disabled
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'alloy']}
                                label="Alloy"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select alloy',
                                  },
                                ]}
                              >
                                <AutoOpenSelect
                                  placeholder="Select Alloy"
                                  loading={alloyLoading}
                                  onPopupScroll={onScrollAlloy}
                                  notFoundContent={
                                    alloyLoading ? 'Loading...' : 'No options'
                                  }
                                  className={getClassNameAlloy(name)}
                                  value={searchAlloy}
                                  onChange={(value, option) =>
                                    handleAlloy(value, option, name)
                                  }
                                  onSearch={handleSearchAlloy}
                                  allowClear
                                  showSearch={true}
                                  filterOption={(input, option) => {
                                    if (!input) return true;
                                    const optionText = (option?.children || '')
                                      .toString()
                                      .replace(/\s+/g, '')
                                      .toLowerCase();
                                    const inputText = input
                                      .replace(/\s+/g, '')
                                      .toLowerCase();
                                    return optionText.includes(inputText);
                                  }}
                                  dropdownRender={menu => <>{menu}</>}
                                >
                                  {[
                                    ...new Map(
                                      alloyList.map(item => [item.id, item]),
                                    ).values(),
                                  ].map((el, index) => (
                                    <Option
                                      key={el.id}
                                      value={el.id}
                                      disabled={el.deleted === 1}
                                    >
                                      {el?.alloy_code} - {el?.standard_name}
                                    </Option>
                                  ))}
                                </AutoOpenSelect>
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'temper']}
                                label="Temper"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select temper',
                                  },
                                ]}
                              >
                                <AutoOpenSelect
                                  placeholder="Select Temper"
                                  loading={temperLoading}
                                  value={searchTemper}
                                  onPopupScroll={onScrollTemper}
                                  notFoundContent={
                                    temperLoading ? 'Loading...' : 'No options'
                                  }
                                  onChange={(value, option) =>
                                    handleTemer(value, option, name)
                                  }
                                  onSearch={handleSearchTemer}
                                  className={getClassNameTemper(name)}
                                  allowClear
                                  showSearch={true}
                                  filterOption={(input, option) => {
                                    if (!input) return true;
                                    const optionText = (option?.children || '')
                                      .toString()
                                      .replace(/\s+/g, '')
                                      .toLowerCase();
                                    const inputText = input
                                      .replace(/\s+/g, '')
                                      .toLowerCase();
                                    return optionText.includes(inputText);
                                  }}
                                  dropdownRender={menu => <>{menu}</>}
                                >
                                  {[
                                    ...new Map(
                                      temperList.map(item => [item.id, item]),
                                    ).values(),
                                  ].map((el, index) => (
                                    <Option
                                      key={el.id}
                                      value={el.id}
                                      disabled={el.deleted === 1}
                                    >
                                      {el.temper_code_new} - {el.temper_code_old}
                                    </Option>
                                  ))}
                                </AutoOpenSelect>
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'length']}
                                label="Length (mm)"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please enter length',
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="0"
                                  maxLength={6}
                                  onChange={() => {
                                    setTimeout(
                                      () => handleFieldChange(name, 'length'),
                                      0,
                                    );
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'pieces']}
                                label="Pieces"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please enter pieces',
                                  },
                                  {
                                    pattern: /^[1-9]\d{0,5}$/,
                                    message: 'Please enter pieces greater than 0',
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="0"
                                  onChange={() => {
                                    setTimeout(
                                      () => handleFieldChange(name, 'pieces'),
                                      0,
                                    );
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'min_weight']}
                                label={`Net Weight - ${form.getFieldValue('tolerance')?.includes('-') ? form.getFieldValue('tolerance').match(/\d+/)?.[0] || '0' : '0'}%`}
                              >
                                <Input placeholder="0.0" disabled />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'net_weight']}
                                label="Net Weight (kg)"
                                rules={[
                                  createNumberValidationRule({
                                    allowNegative: false,
                                    maxBeforeDecimal: 7,
                                    maxAfterDecimal: 3,
                                    allowDecimal: true,
                                  }),
                                ]}
                              >
                                <Input
                                  placeholder="0.0"
                                  onChange={e => {
                                    let value = e.target.value;
                                    if (
                                      /^\d{1,8}(\.\d{1,3})?$/.test(value) ||
                                      value === ''
                                    ) {
                                      handleFieldChange(
                                        name,
                                        'net_weight',
                                        value,
                                      );
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'max_weight']}
                                label={`Net Weight + ${form.getFieldValue('tolerance')?.includes('+') ? form.getFieldValue('tolerance').match(/\d+/)?.[0] || '0' : '0'}%`}
                              >
                                <Input placeholder="0.0" disabled />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <label
                                className="customlabel flex"
                                style={{ marginBottom: '3px' }}
                              >
                                {' '}
                                Nalco -
                                <div className="ml-2">
                                  <Checkbox
                                    disabled={
                                      form.getFieldValue([
                                        'work_order_details',
                                        name,
                                        'id',
                                      ]) && initialValues[name] === true
                                    }
                                    onChange={e => {
                                      const checked = e.target.checked;
                                      form.setFieldsValue({
                                        work_order_details: form
                                          .getFieldValue('work_order_details')
                                          .map((item, index) =>
                                            index === name
                                              ? {
                                                ...item,
                                                modify_nalco_rate: checked,
                                              }
                                              : item,
                                          ),
                                      });
                                    }}
                                  >
                                    Edit Rate
                                  </Checkbox>
                                </div>
                              </label>
                              <Form.Item
                                {...restField}
                                name={[name, 'nalco_rate']}
                                label=""
                                rules={[
                                  createNumberValidationRule({
                                    allowNegative: false,
                                    maxBeforeDecimal: 7,
                                    maxAfterDecimal: 3,
                                    allowDecimal: true,
                                    allowZero: true,
                                  }),
                                ]}
                              >
                                <Input
                                  disabled={
                                    !form.getFieldValue([
                                      'work_order_details',
                                      name,
                                      'modify_nalco_rate',
                                    ])
                                  }
                                  onChange={e =>
                                    handleNalcoChange(e.target.value)
                                  }
                                  placeholder="0.0"
                                />
                              </Form.Item>
                            </Col>
                            {form.getFieldValue([
                              'work_order_details',
                              name,
                              'modify_nalco_rate',
                            ]) && (
                                <Col xs={12} md={8} xl={6} xxl={4}>
                                  <Form.Item
                                    name={[name, 'nalco_rate_change_reason']}
                                    label="Nalco Rate Change Reason"
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Please enter change reason',
                                      },
                                    ]}
                                  >
                                    <Input placeholder="Enter Remarks" />
                                  </Form.Item>
                                </Col>
                              )}
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'conversion']}
                                label="Conversion (Kg)"
                                rules={[
                                  createNumberValidationRule({
                                    allowNegative: false,
                                    maxBeforeDecimal: 10,
                                    maxAfterDecimal: 2,
                                    allowDecimal: true,
                                    allowZero: true,
                                  }),
                                ]}
                              >
                                <Input placeholder="0.0" />
                              </Form.Item>
                            </Col>
                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'packing_charge']}
                                label="Packing Charges (Kg)"
                                rules={[
                                  createNumberValidationRule({
                                    allowNegative: false,
                                    maxBeforeDecimal: 10,
                                    maxAfterDecimal: 2,
                                    allowDecimal: true,
                                    allowZero: true,
                                  }),
                                ]}
                              >
                                <Input placeholder="0" />
                              </Form.Item>
                            </Col>

                            <Col xs={12} md={8} xl={6} xxl={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'jobworks']}
                                label="Surface finish"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select jobwork',
                                  },
                                ]}
                              >
                                <AutoOpenSelect
                                  placeholder="Select Jobwork"
                                  mode="multiple"
                                  value={form.getFieldValue([
                                    'quotation_details',
                                    name,
                                    'jobworks',
                                  ])}
                                  allowClear
                                  showSearch
                                  optionFilterProp="children"
                                  loading={jobWorkLoading}
                                  onChange={(value) => {
                                    const selectedItems = jobWorkdata?.filter(item => value.includes(item.id));
                                    const selectedNames = selectedItems.map(item => item.name);

                                    const engineeringId = jobWorkdata?.find(item => item.name === "Engineering")?.id;
                                    const surfaceTreatmentId = jobWorkdata?.find(item => item.name === "Surface treatment")?.id;

                                    const details = form.getFieldValue("quotation_details") || [];
                                    if (!details[index]) return;
                                    const newDetails = [...details];


                                    // Delete Engineering-related fields if deselected
                                    if (!value.includes(engineeringId)) {
                                      ["cutting", "machining", "deburring"].forEach(opt => {
                                        if (newDetails[index][opt] !== undefined) delete newDetails[index][opt]; // remove boolean
                                        //if (newDetails[index][`${opt}_price`] !== undefined) delete newDetails[index][`${opt}_price`]; // remove price
                                      });
                                    }

                                    // Delete Surface Treatment -related fields if deselected
                                    if (!value.includes(surfaceTreatmentId)) {
                                      ["anodising", "powder_coating", "pvdf"].forEach(opt => {
                                        if (newDetails[index][opt] !== undefined) delete newDetails[index][opt]; // remove boolean
                                        if (newDetails[index][`${opt}_description`] !== undefined) delete newDetails[index][`${opt}_description`]; // remove description
                                        //if (newDetails[index][`${opt}_price`] !== undefined) delete newDetails[index][`${opt}_price`]; // remove price
                                      });
                                    }

                                    form.setFieldsValue({ inquiry_details: newDetails });
                                  }}
                                >
                                  {jobWorkLoading ? (
                                    <Option key="loading" disabled>
                                      Loading...
                                    </Option>
                                  ) : (
                                    jobWorkdata?.map(item => (
                                      <Option key={item.id} value={item.id}>
                                        {item.name}
                                      </Option>
                                    ))
                                  )}
                                </AutoOpenSelect>

                              </Form.Item>
                            </Col>
                            <Form.Item
                              shouldUpdate={(prevValues, currentValues) =>
                                prevValues.work_order_details?.[index]?.jobworks !==
                                currentValues.work_order_details?.[index]?.jobworks
                              }
                              noStyle
                            >
                              {({ getFieldValue }) => {
                                const selectedSurfaceFinish = getFieldValue(["work_order_details", index, "jobworks"]) || [];

                                const selectedItems = jobWorkdata?.filter(item => selectedSurfaceFinish.includes(item.id));


                                // Example: show conditional field if Laser Marking selected
                                const hasEngineering = selectedItems.some(item => item.name === "Engineering");
                                const hasSurfaceTreatment = selectedItems.some(item => item.name === "Surface treatment");

                                return (
                                  <>
                                    {/* --- Engineering Fields --- */}
                                    {hasEngineering && (
                                      <>
                                        <Form.Item
                                          noStyle
                                          shouldUpdate={(prev, curr) =>
                                            prev.work_order_details?.[index]?.cutting !== curr.work_order_details?.[index]?.cutting ||
                                            prev.work_order_details?.[index]?.machining !== curr.work_order_details?.[index]?.machining ||
                                            prev.work_order_details?.[index]?.deburring !== curr.work_order_details?.[index]?.deburring
                                          }
                                        >
                                          {({ getFieldValue }) => {
                                            const cutting = getFieldValue(['work_order_details', index, 'cutting']);
                                            const machining = getFieldValue(['work_order_details', index, 'machining']);
                                            const deburring = getFieldValue(['work_order_details', index, 'deburring']);

                                            return (
                                              <>
                                                <Col xs={12} md={8} xl={6} xxl={6} style={{ marginTop: '24px' }}>
                                                  <Form.Item
                                                    name={[name, "cutting"]}
                                                    valuePropName="checked"
                                                    noStyle
                                                  >
                                                    <Checkbox>Cutting</Checkbox>
                                                  </Form.Item>

                                                  <Form.Item
                                                    name={[name, "machining"]}
                                                    valuePropName="checked"
                                                    noStyle
                                                  >
                                                    <Checkbox>Machining</Checkbox>
                                                  </Form.Item>

                                                  <Form.Item
                                                    name={[name, "deburring"]}
                                                    valuePropName="checked"
                                                    noStyle
                                                  >
                                                    <Checkbox>Deburring</Checkbox>
                                                  </Form.Item>
                                                </Col>
                                                {cutting && (
                                                  <Col xs={12} md={8} xl={3}>
                                                    <Form.Item
                                                      label="Cutting Price"
                                                      name={[name, 'cutting_price']}
                                                      rules={[{ required: true, message: 'Enter Cutting Price' }]}
                                                    >
                                                      <Input placeholder="0.0" />
                                                    </Form.Item>
                                                  </Col>
                                                )}

                                                {machining && (
                                                  <Col xs={12} md={8} xl={3}>
                                                    <Form.Item
                                                      label="Machining Price"
                                                      name={[name, 'machining_price']}
                                                      rules={[{ required: true, message: 'Enter Machining Price' }]}
                                                    >
                                                      <Input placeholder="0.0" />
                                                    </Form.Item>
                                                  </Col>
                                                )}

                                                {deburring && (
                                                  <Col xs={12} md={8} xl={3}>
                                                    <Form.Item
                                                      label="Deburring Price"
                                                      name={[name, 'deburring_price']}
                                                      rules={[{ required: true, message: 'Enter Deburring Price' }]}
                                                    >
                                                      <Input placeholder="0.0" />
                                                    </Form.Item>
                                                  </Col>
                                                )}
                                              </>
                                            );
                                          }}
                                        </Form.Item>
                                      </>
                                    )}
                                    {/* --- Surface Treatment Fields --- */}
                                    {hasSurfaceTreatment && (
                                      <>


                                        {/* Nested conditional price fields */}
                                        <Form.Item
                                          noStyle
                                          shouldUpdate={(prev, curr) =>
                                            prev.work_order_details?.[index]?.anodising !== curr.work_order_details?.[index]?.anodising ||
                                            prev.work_order_details?.[index]?.powder_coating !== curr.work_order_details?.[index]?.powder_coating ||
                                            prev.work_order_details?.[index]?.pvdf !== curr.work_order_details?.[index]?.pvdf
                                          }
                                        >
                                          {({ getFieldValue }) => {
                                            const anodising = getFieldValue(["work_order_details", index, "anodising"]);
                                            const powder_coating = getFieldValue(["work_order_details", index, "powder_coating"]);
                                            const pvdf = getFieldValue(["work_order_details", index, "pvdf"]);
                                            return (
                                              <>
                                                <Col xs={12} md={8} xl={6} xxl={6} style={{ marginTop: '24px' }}>
                                                  <Form.Item
                                                    name={[name, "anodising"]}
                                                    valuePropName="checked"
                                                    noStyle
                                                  >
                                                    <Checkbox>Anodising</Checkbox>
                                                  </Form.Item>

                                                  <Form.Item
                                                    name={[name, "powder_coating"]}
                                                    valuePropName="checked"
                                                    noStyle
                                                  >
                                                    <Checkbox>Powder Coating</Checkbox>
                                                  </Form.Item>

                                                  <Form.Item
                                                    name={[name, "pvdf"]}
                                                    valuePropName="checked"
                                                    noStyle
                                                  >
                                                    <Checkbox>PVDF</Checkbox>
                                                  </Form.Item>
                                                </Col>
                                                {anodising && (
                                                  <>
                                                    <Col xs={12} md={8} xl={3}>
                                                      <Form.Item
                                                        label="Anodising Description"
                                                        name={[name, "anodising_description"]}
                                                        rules={[{ required: true, message: "Enter Anodising Description" }]}
                                                      >
                                                        <Input placeholder="Enter Anodising Description" />
                                                      </Form.Item>
                                                    </Col>
                                                    <Col xs={12} md={8} xl={3}>
                                                      <Form.Item
                                                        label="Anodising Price"
                                                        name={[name, "anodising_price"]}
                                                        rules={[{ required: true, message: "Enter Anodising Price" }]}
                                                      >
                                                        <Input placeholder="Enter Anodising Price" />
                                                      </Form.Item>
                                                    </Col>
                                                  </>
                                                )}

                                                {powder_coating && (
                                                  <>
                                                    <Col xs={12} md={8} xl={4}>
                                                      <Form.Item
                                                        label="Powder Coating Description"
                                                        name={[name, "powder_coating_description"]}
                                                        rules={[{ required: true, message: "Enter Powder Coating Description" }]}
                                                      >
                                                        <Input placeholder="Enter Powder Coating Description" />
                                                      </Form.Item>
                                                    </Col>
                                                    <Col xs={12} md={8} xl={3}>
                                                      <Form.Item
                                                        label="Powder Coating Price"
                                                        name={[name, "powder_coating_price"]}
                                                        rules={[{ required: true, message: "Enter Powder Coating Price" }]}
                                                      >
                                                        <Input placeholder="Enter Powder Coating Price" />
                                                      </Form.Item>
                                                    </Col>
                                                  </>
                                                )}

                                                {pvdf && (
                                                  <>
                                                    <Col xs={12} md={8} xl={3}>
                                                      <Form.Item
                                                        label="PVDF Description"
                                                        name={[name, "pvdf_description"]}
                                                        rules={[{ required: true, message: "Enter PVDF Description" }]}
                                                      >
                                                        <Input placeholder="Enter PVDF Description" />
                                                      </Form.Item>
                                                    </Col>
                                                    <Col xs={12} md={8} xl={3}>
                                                      <Form.Item
                                                        label="PVDF Price"
                                                        name={[name, "pvdf_price"]}
                                                        rules={[{ required: true, message: "Enter PVDF Price" }]}
                                                      >
                                                        <Input placeholder="Enter PVDF Price" />
                                                      </Form.Item>
                                                    </Col>
                                                  </>
                                                )}
                                              </>
                                            );
                                          }}
                                        </Form.Item>
                                      </>
                                    )}
                                  </>
                                );
                              }}
                            </Form.Item>
                            {currentUser.permissions.permissions.workorder.includes(
                              'change_profile_over_weight',
                            ) && (
                                <Col xs={12} md={8} xl={4} xxl={4}>
                                  <div
                                    style={{
                                      padding: '5px',
                                      borderRadius: '8px',
                                      marginTop: '10px',
                                    }}
                                  >
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'die_over_weight']}
                                      value={
                                        form.getFieldValue([
                                          'work_order_details',
                                          name,
                                          'die_over_weight',
                                        ])
                                          ? {
                                            value: form.getFieldValue([
                                              'work_order_details',
                                              name,
                                              'die_over_weight',
                                            ]),
                                            label: '',
                                          }
                                          : undefined
                                      }
                                      valuePropName="checked"
                                    >
                                      <Checkbox>
                                        Allow Access Profile Weight
                                      </Checkbox>
                                    </Form.Item>
                                  </div>
                                </Col>
                              )}
                          </Row>
                        </Col>
                        <Col>
                          <div className="addwrp">
                            {index !== 0 && (
                              <Button
                                type="primary"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              />
                            )}
                          </div>
                        </Col>
                      </Row>
                    </>
                  ))}
                  <Form.Item>
                    <div className="w-100 flex justify-end ">
                      <Button
                        type="primary"
                        onClick={() => {
                          if (fields.length < 500) {
                            dispatch(resetDieProfileData());
                            dispatch(
                              fetchDieProfileDropdown({
                                page: 1,
                                pagesize: 100,
                              }),
                            );
                            setSearchDieProfile('');
                            add({ nalco_rate: pricePerKg, jobworks: [1] });
                          } else {
                            message.error('You can only add up to 15 records.');
                          }
                        }}
                        icon={<PlusOutlined />}
                      >
                        Add Item
                      </Button>
                    </div>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
          <div className="flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              icon={<EditOutlined />}
              loading={btnLoading}
            >
              Edit Work Order
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}

export default EditWorkOrder;